<template>
  <div class="card mb-4" :class="{ 'border-secondary opacity-50': form.discarded }">
    <div class="card-header d-flex align-items-center justify-content-between">
      <div>
        <i class="fas fa-file-pdf text-danger mr-1"></i>
        <strong>{{ policy.filename }}</strong>
        <span v-if="policy.warning" class="badge badge-warning ml-2">
          <i class="fas fa-exclamation-triangle mr-1"></i>Atenção
        </span>
        <span v-if="policy.error" class="badge badge-danger ml-2">
          <i class="fas fa-times mr-1"></i>Erro
        </span>
      </div>
      <b-form-checkbox
        v-model="form.discarded"
        switch
        class="mb-0"
      >
        {{ form.discarded ? 'Descartado' : 'Manter' }}
      </b-form-checkbox>
    </div>

    <div class="card-body" v-if="!form.discarded">
      <div v-if="policy.warning" class="alert-custom alert-warning-custom mb-3">
        {{ policy.warning }}
      </div>
      <div v-if="policy.error" class="alert-custom alert-danger-custom mb-3">
        {{ policy.message }}
      </div>

      <div class="row">
        <!-- Tipo de Seguro -->
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">Tipo de Seguro <span class="text-danger">*</span></label>
          <select v-model="form.insurance_type_id" class="form-control" @change="onTypeChange">
            <option :value="null" disabled>Selecione...</option>
            <option v-for="t in insuranceTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
          </select>
        </div>

        <!-- Subtipo / Modalidade -->
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">Subtipo / Modalidade <span class="text-danger">*</span></label>
          <select v-model="form.insurance_category_id" class="form-control" :disabled="!form.insurance_type_id">
            <option :value="null">Selecione...</option>
            <template v-for="group in groupedCategories">
              <!-- Parent has children: render as optgroup -->
              <optgroup v-if="group.children.length" :key="'g' + group.parent.id" :label="group.parent.tecnic_name || group.parent.name">
                <option v-for="c in group.children" :key="c.id" :value="c.id">
                  {{ c.tecnic_name || c.name }}
                </option>
              </optgroup>
              <!-- Standalone parent (no children) -->
              <option v-else :key="group.parent.id" :value="group.parent.id">
                {{ group.parent.tecnic_name || group.parent.name }}
              </option>
            </template>
          </select>
        </div>

        <!-- Seguradora -->
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">Seguradora <span class="text-danger">*</span></label>
          <select v-model="form.insurer_selected" class="form-control">
            <option :value="null" disabled>Selecione...</option>
            <option v-for="(name, identifier) in insurers" :key="identifier" :value="identifier">
              {{ name }}
            </option>
          </select>
        </div>

        <!-- Outra Seguradora -->
        <div v-if="form.insurer_selected === 'other'" class="col-md-4 mb-3">
          <label class="dbo-ui">Nome da Seguradora <span class="text-danger">*</span></label>
          <b-form-input v-model="form.other_insurer" placeholder="Nome da seguradora" />
        </div>

        <!-- Corretora -->
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">Corretora</label>
          <b-form-input v-model="form.insurance_broker" />
        </div>

        <!-- Nº Apólice (external_number) -->
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">Número da Apólice</label>
          <b-form-input v-model="form.external_number" />
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-2">
          <h6 class="form-subheader"><span>Dados do Tomador</span></h6>
        </div>
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">CPF / CNPJ <span class="text-danger">*</span></label>
          <b-form-input v-model="form.policy_holder_document" placeholder="Somente dígitos" />
        </div>
        <div class="col-md-8 mb-3">
          <label class="dbo-ui">Razão Social / Nome <span class="text-danger">*</span></label>
          <b-form-input v-model="form.policy_holder_name" />
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-2">
          <h6 class="form-subheader"><span>Dados do Segurado</span></h6>
        </div>
        <div class="col-md-4 mb-3">
          <label class="dbo-ui">CPF / CNPJ <span class="text-danger">*</span></label>
          <b-form-input v-model="form.insured_document" placeholder="Somente dígitos" />
        </div>
        <div class="col-md-8 mb-3">
          <label class="dbo-ui">Razão Social / Nome <span class="text-danger">*</span></label>
          <b-form-input v-model="form.insured_name" />
        </div>
      </div>

      <div class="row">
        <div class="col-12 mb-2">
          <h6 class="form-subheader"><span>Dados da Apólice</span></h6>
        </div>

        <div class="col-md-3 mb-3">
          <label class="dbo-ui">IS / Valor de Cobertura (R$) <span class="text-danger">*</span></label>
          <money v-bind="money" v-model="form.coverage_value" class="form-control" />
        </div>

        <div class="col-md-3 mb-3">
          <label class="dbo-ui">Prêmio (R$) <span class="text-danger">*</span></label>
          <money v-bind="money" v-model="form.insurance_premium" class="form-control" />
        </div>

        <div class="col-md-3 mb-3">
          <label class="dbo-ui">Início de Vigência <span class="text-danger">*</span></label>
          <b-form-input v-model="form.validity_start" type="date" />
        </div>

        <div class="col-md-3 mb-3">
          <label class="dbo-ui">Término de Vigência <span class="text-danger">*</span></label>
          <b-form-input v-model="form.validity_expiration" type="date" />
        </div>

        <div class="col-md-4 mb-3">
          <label class="dbo-ui">Número do Contrato / Processo</label>
          <b-form-input v-model="form.contract_number" />
        </div>

        <div class="col-md-4 mb-3 d-flex align-items-end pb-1">
          <b-form-checkbox v-model="form.renewable">Renovável automaticamente</b-form-checkbox>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
export default {
  name: 'PolicyReviewCard',
  props: {
    policy: {
      type: Object,
      required: true,
    },
    insuranceTypes: {
      type: Array,
      default: () => [],
    },
    insuranceCategories: {
      type: Array,
      default: () => [],
    },
    insurers: {
      type: Object,
      default: () => ({}),
    },
    index: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      money: {
        decimal: ',',
        thousands: '.',
        prefix: 'R$ ',
        suffix: ' ',
        precision: 2,
        masked: false,
      },
      form: {
        discarded:               false,
        insurance_type_id:       this.policy.insurance_type_id       ?? null,
        insurance_category_id:   this.policy.insurance_category_id   ?? null,
        insurer_selected:        this.policy.insurer_selected         ?? null,
        other_insurer:           this.policy.other_insurer            ?? null,
        insurance_broker:        this.policy.insurance_broker         ?? null,
        external_number:         this.policy.external_number         ?? this.policy.policy_number ?? null,
        contract_number:         this.policy.contract_number          ?? null,
        policy_holder_document:  this.policy.policy_holder_document  ?? null,
        policy_holder_name:      this.policy.policy_holder_name      ?? null,
        insured_document:        this.policy.insured_document         ?? null,
        insured_name:            this.policy.insured_name             ?? null,
        coverage_value:          this.policy.coverage_value           ?? 0,
        insurance_premium:       this.policy.insurance_premium        ?? 0,
        validity_start:          this.policy.validity_start           ?? null,
        validity_expiration:     this.policy.validity_expiration      ?? null,
        renewable:               this.policy.renewable                ?? false,
      },
    };
  },
  computed: {
    filteredCategories() {
      if (!this.form.insurance_type_id) return [];
      return this.insuranceCategories.filter(
        (c) => c.insurance_type_id === this.form.insurance_type_id
      );
    },
    groupedCategories() {
      const all = this.filteredCategories;
      const parents = all.filter((c) => !c.parent_insurance_category_id);
      return parents.map((parent) => ({
        parent,
        children: all.filter((c) => c.parent_insurance_category_id === parent.id),
      }));
    },
    // IDs that actually appear as <option> elements in the select
    selectableCategoryIds() {
      const ids = new Set();
      this.groupedCategories.forEach(({ parent, children }) => {
        if (children.length) {
          children.forEach((c) => ids.add(c.id));
        } else {
          ids.add(parent.id);
        }
      });
      return ids;
    },
    hasMissingRequired() {
      if (this.form.discarded) return false;
      return (
        !this.form.insurance_type_id ||
        !this.form.insurance_category_id ||
        !this.form.coverage_value ||
        !this.form.policy_holder_document ||
        !this.form.policy_holder_name ||
        !this.form.insured_document ||
        !this.form.insured_name ||
        !this.form.insurance_premium ||
        !this.form.validity_start ||
        !this.form.validity_expiration
      );
    },
  },
  watch: {
    // When categories load (async or already present on mount), clear the form value
    // if the backend returned a parent-only ID that is not actually selectable in the dropdown.
    // immediate: true ensures it also runs on first render, not only on changes.
    selectableCategoryIds: {
      immediate: true,
      handler(ids) {
        if (this.form.insurance_category_id !== null && !ids.has(this.form.insurance_category_id)) {
          this.form.insurance_category_id = null;
        }
      },
    },
  },
  methods: {
    onTypeChange() {
      this.form.insurance_category_id = null;
      this.$emit('type-change', this.form.insurance_type_id);
    },
    getFormData() {
      return { ...this.form, filename: this.policy.filename };
    },
  },
};
</script>
